import { useTheme } from '../hooks/useTheme'
import { useTranslation } from '../i18n'
import { Button } from '../components/ui'
import { TourIcon, type TourIconId } from './tourVisuals'

/** Welcome slide icons (index-aligned with translation slides). */
const WELCOME_SLIDE_ICONS: TourIconId[] = ['welcome', 'plan', 'schedule']

type TourWelcomeCopy = {
  skip: string
  next: string
  welcome: {
    title: string
    startTour: string
    slides: Array<{ title: string; body: string }>
  }
}

interface Props {
  copyKey: string
  slideIndex: number
  slideCount: number
  onNext: () => void
  onSkip: () => void
}

export default function WelcomeTourModal({
  copyKey,
  slideIndex,
  slideCount,
  onNext,
  onSkip,
}: Props) {
  const theme = useTheme()
  const { t } = useTranslation()
  const copy = (t.tour as Record<string, TourWelcomeCopy>)[copyKey]
  const slides = copy?.welcome?.slides ?? []
  const isLast = slideIndex >= slideCount - 1
  const slide = slides[slideIndex] ?? slides[0]
  const iconId = WELCOME_SLIDE_ICONS[slideIndex] ?? WELCOME_SLIDE_ICONS[0]

  if (!copy || !slide) return null

  return (
    <div
      className="fixed inset-0 z-[90] flex flex-col"
      style={{ backgroundColor: theme.bgColor }}
      role="dialog"
      aria-modal="true"
      aria-label={copy.welcome.title}
    >
      <div
        className="flex items-center justify-end px-4"
        style={{ paddingTop: 'calc(0.75rem + env(safe-area-inset-top, 0px))' }}
      >
        <button
          type="button"
          onClick={onSkip}
          className="min-h-11 min-w-11 text-sm font-semibold px-3 rounded-xl active:opacity-70 transition-opacity"
          style={{ color: theme.text.muted }}
        >
          {copy.skip}
        </button>
      </div>

      <div className="flex-1 flex flex-col justify-center px-6 pb-4 overflow-hidden">
        <div
          key={slideIndex}
          className="flex flex-col items-center text-center"
          style={{ animation: 'tour-slide-in 200ms ease-out' }}
        >
          <div
            className="w-28 h-28 rounded-[2rem] flex items-center justify-center mb-8"
            style={{
              backgroundColor: `${theme.buttonColor}18`,
              color: theme.buttonColor,
              boxShadow: `0 8px 24px ${theme.buttonColor}14`,
            }}
            aria-hidden
          >
            <TourIcon id={iconId} size={44} />
          </div>

          <p
            className="text-xs font-bold uppercase tracking-widest mb-3"
            style={{ color: theme.text.muted }}
          >
            {copy.welcome.title}
          </p>
          <h2
            className="text-2xl font-extrabold leading-tight mb-3 max-w-[20rem]"
            style={{ color: theme.text.primary, letterSpacing: '-0.4px' }}
          >
            {slide.title}
          </h2>
          <p
            className="text-sm leading-relaxed max-w-[20rem]"
            style={{ color: theme.text.secondary }}
          >
            {slide.body}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mt-10">
          {Array.from({ length: slideCount }).map((_, i) => (
            <span
              key={i}
              className="h-1.5 rounded-full transition-all duration-200"
              style={{
                width: i === slideIndex ? 24 : 8,
                backgroundColor: i === slideIndex ? theme.buttonColor : theme.cardBorder,
              }}
            />
          ))}
        </div>
      </div>

      <div
        className="px-4 pt-3 shrink-0"
        style={{
          borderTop: `1px solid ${theme.cardBorder}`,
          paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))',
        }}
      >
        <Button type="button" variant="primary" size="lg" fullWidth onClick={onNext}>
          {isLast ? copy.welcome.startTour : copy.next}
        </Button>
      </div>
    </div>
  )
}

// Separate file recommended for hooks to avoid fast-refresh issues
// eslint-disable-next-line react-refresh/only-export-components
export function useWelcomeSlideCount(copyKey: string | null | undefined): number {
  const { t } = useTranslation()
  if (!copyKey) return 0
  const copy = (t.tour as Record<string, TourWelcomeCopy>)[copyKey]
  return copy?.welcome?.slides?.length ?? 0
}
